"use client"

import { useState, useEffect, useRef } from "react"
import Stripe from '../../components/payment/Stripe';
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import billingProd from '../../constants/Billing.json';
import billingTest from '../../constants/BillingTest.json';
import { useTranslation } from 'react-i18next';
import { creditsService } from '../../services/credits';
import { useRNBridge } from '../../hooks/useRNBridge';
import { useAuth } from '../../context/AuthContext';
import { track } from '../../third-party/MixPanel';
import { useLog } from '../../context/LogContext';

// Set to TRUE for Expo Go testing (uses Test Store: $rc_* products)
// Set to FALSE for production builds (uses real products: com.br4.tarot.credits.*)
const USE_TEST_STORE = false;

const billing = USE_TEST_STORE ? billingTest : billingProd;


export default function Billing({ onSuccess, padding = 0 }: { onSuccess: () => void, padding?: number }) {
    const { t } = useTranslation();
    const { sendMessage, lastMessage } = useRNBridge();
    const { user, refreshUserData } = useAuth();
    const { log } = useLog();

    const [selectedPlan, setSelectedPlan] = useState<any>(billing.plans[0]); // Default to 10
    const [selectedProductId, setSelectedProductId] = useState<string>(billing.plans[0].productId);// Default to 10
    const [displayPriceSelector, setDisplayPriceSelector] = useState(true);
    const [displayPaymentSelector, setDisplayPaymentSelector] = useState(false);
    const [isProcessingPurchase, setIsProcessingPurchase] = useState(false);
    const [purchaseStatusMessage, setPurchaseStatusMessage] = useState<string>('');
    const [isPollingCredits, setIsPollingCredits] = useState(false);
    const [isUserIdSet, setIsUserIdSet] = useState(false);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const pollingStartTimeRef = useRef<number | null>(null);
    const initialCreditsRef = useRef<number | null>(null);
    const isSettingUserIdRef = useRef(false); // Prevent duplicate setUserId calls

    // Add error boundary logging
    useEffect(() => {
        log('info', '🟣 [Billing] Component mounted', {
            hasUser: !!user,
            hasSendMessage: !!sendMessage,
            lastMessageType: lastMessage?.type || lastMessage?.action
        });
        return () => {
            log('info', '🟣 [Billing] Component unmounting');
        };
    }, []);

    // Log all lastMessage updates for debugging
    useEffect(() => {
        if (lastMessage) {
            log('debug', '📨 [Billing] LastMessage updated', {
                type: lastMessage.type || lastMessage.action,
                message: lastMessage.message,
                error: lastMessage.error,
                success: lastMessage.success,
                data: lastMessage.data,
                fullMessage: lastMessage
            });
        }
    }, [lastMessage]);

    // Use sessionStorage to persist across component remounts (drawer open/close)
    const getLastSetUserId = () => {
        try {
            return sessionStorage.getItem('rc_last_user_id');
        } catch {
            return null;
        }
    };

    const setLastSetUserId = (userId: string | null) => {
        try {
            if (userId) {
                sessionStorage.setItem('rc_last_user_id', userId);
            } else {
                sessionStorage.removeItem('rc_last_user_id');
            }
        } catch {
            // Ignore storage errors
        }
    };

    // CRITICAL: Ensure user ID is set in RevenueCat when billing page opens
    // With race condition prevention
    useEffect(() => {
        if (user?._id && sendMessage) {
            const lastSetUserId = getLastSetUserId();

            // Prevent duplicate requests for the same user ID
            if (isSettingUserIdRef.current || lastSetUserId === user._id) {
                log('info', '⚪ [Billing] Skipping duplicate setUserId request', { userId: user._id });
                // Still set the state to enabled since we've already done this
                setIsUserIdSet(true);
                return;
            }

            log('info', '📱 [Billing] Sending SET_USER_ID to React Native app', {
                userId: user._id,
                email: user.email,
                firstName: user.firstname,
                lastName: user.lastname
            });

            isSettingUserIdRef.current = true;
            setIsUserIdSet(false); // Reset state while setting

            sendMessage({
                action: 'setUserId',
                type: 'SET_USER_ID',
                payload: user._id,
                userId: user._id,
                userProfile: {
                    email: user.email,
                    firstName: user.firstname,
                    lastName: user.lastname
                }
            });

            // Fallback: Enable button after 3 seconds even if no confirmation received
            // This prevents UI from being stuck if message passing fails
            const fallbackTimer = setTimeout(() => {
                log('warn', '⚠️  [Billing] Fallback timeout reached, enabling button');
                setIsUserIdSet(true);
                isSettingUserIdRef.current = false;
            }, 3000);

            return () => {
                clearTimeout(fallbackTimer);
            };
        } else {
            log('warn', '⚠️  [Billing] Cannot send user ID', { hasUser: !!user, hasUserId: !!user?._id, hasSendMessage: !!sendMessage });
            setIsUserIdSet(false);
            isSettingUserIdRef.current = false;
        }
    }, [user?._id, sendMessage]); // Re-run if user ID or sendMessage changes

    // Listen for user ID set confirmation from React Native
    useEffect(() => {
        if (lastMessage?.action === 'userIdSet' || lastMessage?.type === 'USER_ID_SET') {
            log('info', '[Billing] User ID set confirmation', lastMessage);
            if (lastMessage.success) {
                log('info', '✅ [Billing] User ID successfully set in RevenueCat', { userId: lastMessage.userId });
                setIsUserIdSet(true);
                setLastSetUserId(lastMessage.userId || user?._id || null);
                isSettingUserIdRef.current = false;
            } else {
                log('error', '❌ [Billing] Failed to set user ID in RevenueCat', { error: lastMessage.error });
                setIsUserIdSet(false);
                isSettingUserIdRef.current = false;
                // Allow retry on failure
                setLastSetUserId(null);
            }
        }
    }, [lastMessage, user?._id]);

    // Listen for purchase progress messages from React Native
    useEffect(() => {
        if (lastMessage?.action === 'purchaseProgress' || lastMessage?.type === 'PURCHASE_PROGRESS') {
            log('info', '[Billing] Purchase progress', lastMessage);
            if (lastMessage.message) {
                setPurchaseStatusMessage(lastMessage.message);
            }
        }
    }, [lastMessage]);

    // Listen for purchase results from React Native
    useEffect(() => {
        if (lastMessage?.action === 'purchaseResult' || lastMessage?.type === 'PURCHASE_RESULT') {
            setIsProcessingPurchase(false);

            if (lastMessage.success) {
                log('info', '[Billing] Purchase successful, starting credit polling', { message: lastMessage.message });

                track('purchase_success', {
                    credits: selectedPlan.credits,
                    price: selectedPlan.price,
                    productId: selectedPlan.productId,
                    revenueCatProductId: selectedPlan.revenueCatProductId,
                    paymentMethod: 'mobile_in_app'
                });

                // Show success message and close modal immediately
                setPurchaseStatusMessage(t('billing.purchaseSuccessPolling'));

                // Start background polling for credit updates
                startCreditPolling();

                // Close the billing UI after a brief delay to show the success message
                setTimeout(() => {
                    handleSuccess();
                }, 2000);
            } else {
                log('error', '❌ [Billing] Purchase failed', {
                    error: lastMessage.error,
                    data: lastMessage.data,
                    fullMessage: lastMessage
                });

                const isCancelled = lastMessage.error && lastMessage.error.includes('cancelled');

                track(isCancelled ? 'purchase_cancelled' : 'purchase_failed', {
                    credits: selectedPlan.credits,
                    price: selectedPlan.price,
                    productId: selectedPlan.productId,
                    revenueCatProductId: selectedPlan.revenueCatProductId,
                    paymentMethod: 'mobile_in_app',
                    error: lastMessage.error,
                    errorType: isCancelled ? 'user_cancelled' : 'payment_error'
                });

                setPurchaseStatusMessage('');
                // You can show an error toast here if needed
                if (!isCancelled) {
                    // Show error to user unless they cancelled
                    log('error', t('billing.purchaseFailed', { error: lastMessage.error }));
                }
            }
        }
    }, [lastMessage]);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            stopCreditPolling();
        };
    }, []);

    // Monitor credit changes during polling
    useEffect(() => {
        if (isPollingCredits && user && initialCreditsRef.current !== null) {
            const currentCredits = user.credits ?? 0;
            log('debug', '[Billing] Polling - Credit check', { currentCredits, initialCredits: initialCreditsRef.current });

            // Check if credits have increased
            if (currentCredits > initialCreditsRef.current) {
                log('info', '[Billing] Credits updated successfully! Stopping background polling');

                const creditsAdded = currentCredits - initialCreditsRef.current;
                track('credits_updated', {
                    previousCredits: initialCreditsRef.current,
                    newCredits: currentCredits,
                    creditsAdded: creditsAdded,
                    purchaseCredits: selectedPlan.credits,
                    price: selectedPlan.price
                });

                stopCreditPolling();
                // No need to call handleSuccess again - user already moved on
            }
        }
    }, [user?.credits, isPollingCredits]);

    const startCreditPolling = () => {
        // Store initial credit count
        initialCreditsRef.current = user?.credits ?? 0;
        pollingStartTimeRef.current = Date.now();
        setIsPollingCredits(true);

        log('info', '[Billing] Starting background credit polling', { initialCredits: initialCreditsRef.current });

        // Poll every 2 seconds
        pollingIntervalRef.current = setInterval(async () => {
            const elapsedTime = Date.now() - (pollingStartTimeRef.current ?? 0);
            const maxPollingTime = 60000; // 60 seconds max

            log('debug', '[Billing] Polling for credit update', { elapsedSeconds: Math.round(elapsedTime / 1000) });

            if (elapsedTime > maxPollingTime) {
                log('warn', '[Billing] Polling timeout reached - credits will update eventually');
                stopCreditPolling();
                return;
            }

            // Refresh user data
            try {
                await refreshUserData();
            } catch (error) {
                log('error', '[Billing] Error refreshing user data during polling', { error });
            }
        }, 2000);
    };

    const stopCreditPolling = () => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        setIsPollingCredits(false);
        setPurchaseStatusMessage('');
        pollingStartTimeRef.current = null;
        initialCreditsRef.current = null;
    };

    const handlePriceSelector = () => {
        log('info', '🔴 [Billing] handlePriceSelector called - Buy button clicked');

        // Prevent multiple simultaneous purchase requests
        if (isProcessingPurchase) {
            log('warn', '⚠️  [Billing] Purchase already in progress, ignoring duplicate request');
            return;
        }

        log('info', '🔴 [Billing] Selected plan', {
            credits: selectedPlan.credits,
            price: selectedPlan.price,
            productId: selectedPlan.productId,
            revenueCatProductId: selectedPlan.revenueCatProductId
        });

        track('purchase_initiated', {
            credits: selectedPlan.credits,
            price: selectedPlan.price,
            productId: selectedPlan.productId,
            revenueCatProductId: selectedPlan.revenueCatProductId,
            paymentMethod: 'mobile_in_app'
        });

        log('info', '🔴 [Billing] Setting isProcessingPurchase to true');
        setIsProcessingPurchase(true);

        log('info', '🔴 [Billing] Calling creditsService.sendPaymentRequestToReactNative');
        creditsService.sendPaymentRequestToReactNative(selectedPlan, sendMessage);
        log('info', '🔴 [Billing] Payment request sent to React Native');
    }

    const handleSuccess = () => {
        setDisplayPriceSelector(true)
        setDisplayPaymentSelector(false)
        onSuccess();
    }

    const displayPriceComponent = () => {
        return (<Box sx={{ p: padding }}>
            <Typography variant="h2" sx={{ textAlign: 'center', mb: 2, fontSize: '1.2rem' }}>
                {t('billing.title')}
            </Typography>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 2,
                mb: 2,
                p: 2
            }}>
                {billing.plans.map((plan) => (
                    <Box
                        key={plan.credits}
                        onClick={() => {
                            track('billing_plan_selected', {
                                credits: plan.credits,
                                price: plan.price,
                                productId: plan.productId,
                                revenueCatProductId: plan.revenueCatProductId
                            });
                            setSelectedPlan(plan)
                            setSelectedProductId(plan.productId)
                        }}
                        sx={{
                            border: selectedPlan === plan.credits
                                ? '2px solid purple'
                                : '1px solid rgba(0, 0, 0, 0.12)',
                            borderRadius: 2,
                            p: 2,
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                                borderColor: 'purple',
                                transform: 'scale(1.02)'
                            }
                        }}
                    >
                        <Typography variant="h5" sx={{ mb: 0.5 }}>{plan.credits}</Typography>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('billing.credits')}</Typography>
                        <Typography variant="subtitle1">R${plan.price}</Typography>
                        {plan.credits === 25 && (
                            <Typography variant="caption" sx={{ mb: 0.5, color: 'text.secondary' }}>
                                {t('billing.betterValue')}
                            </Typography>
                        )}
                        {plan.credits === 60 && (
                            <Typography variant="caption" sx={{ mb: 0.5, color: 'text.secondary' }}>
                                {t('billing.bestValue')}
                            </Typography>
                        )}

                    </Box>
                ))}
            </Box>

            <Typography sx={{
                textAlign: 'center',
                color: 'text.secondary',
                mb: 2,
                fontSize: '0.75rem'
            }}>
                {!purchaseStatusMessage && t('billing.creditsNeverExpire')}
                {!isProcessingPurchase && purchaseStatusMessage && `✓${purchaseStatusMessage}`}
                &nbsp;
            </Typography>

            {/* Error Display - Always visible when there's an error */}
            {lastMessage?.error && (
                <Box sx={{
                    mb: 2,
                    p: 2,
                    bgcolor: '#ffebee',
                    borderRadius: 1,
                    border: '2px solid #d32f2f',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    maxHeight: '400px',
                    overflowY: 'auto'
                }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#d32f2f' }}>
                        ❌ ERROR DETAILS
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{
                            padding: '8px',
                            bgcolor: '#ffffff',
                            borderRadius: '4px',
                            border: '1px solid #ef5350',
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap'
                        }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#d32f2f' }}>Error Message:</div>
                            {String(lastMessage.error)}
                        </div>

                        {lastMessage?.data && (
                            <div style={{
                                padding: '8px',
                                bgcolor: '#ffffff',
                                borderRadius: '4px',
                                border: '1px solid #ef5350',
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                                fontSize: '0.70rem'
                            }}>
                                <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#d32f2f' }}>Additional Data:</div>
                                {(() => {
                                    try {
                                        return JSON.stringify(lastMessage.data, null, 2);
                                    } catch (e) {
                                        return '[Unable to serialize data]';
                                    }
                                })()}
                            </div>
                        )}

                        <div style={{ fontSize: '0.70rem', color: '#666', fontStyle: 'italic' }}>
                            Message Type: {String(lastMessage?.type || lastMessage?.action || 'Unknown')}
                        </div>
                    </div>
                </Box>
            )}

            {/* Debug State Info - Only in Dev Mode */}
            {USE_TEST_STORE && (
                <Box sx={{
                    mb: 2,
                    p: 1.5,
                    bgcolor: '#f5f5f5',
                    borderRadius: 1,
                    border: '1px solid #ddd',
                    fontFamily: 'monospace',
                    fontSize: '0.70rem'
                }}>
                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        flexWrap: 'wrap',
                        fontSize: '0.70rem'
                    }}>
                        <div style={{ color: '#666' }}>
                            User ID: <span style={{ color: isUserIdSet ? '#4caf50' : '#f44336', fontWeight: 'bold' }}>{isUserIdSet ? '✓' : '✗'}</span>
                        </div>
                        <div style={{ color: '#666' }}>
                            Processing: <span style={{ color: isProcessingPurchase ? '#ff9800' : '#4caf50', fontWeight: 'bold' }}>{isProcessingPurchase ? '⏳' : '✓'}</span>
                        </div>
                        <div style={{ color: '#666' }}>
                            Polling: <span style={{ color: isPollingCredits ? '#ff9800' : '#4caf50', fontWeight: 'bold' }}>{isPollingCredits ? '⏳' : '✓'}</span>
                        </div>
                    </div>
                </Box>
            )}







            <Button
                variant="contained"
                fullWidth
                onClick={() => handlePriceSelector()}
                disabled={isProcessingPurchase || !isUserIdSet}
                sx={{
                    bgcolor: 'black',
                    color: 'white',
                    py: 1.5,
                    '&:hover': {
                        bgcolor: '#333'
                    },
                    '&:disabled': {
                        bgcolor: '#666',
                        color: '#ccc'
                    }
                }}
            >
                {!isUserIdSet && !isProcessingPurchase ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                        <CircularProgress size={20} sx={{ color: 'white' }} />
                        <Typography variant="body2">Preparing...</Typography>
                    </Box>
                ) : isProcessingPurchase ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                        <CircularProgress size={24} sx={{ color: 'white' }} />
                        {purchaseStatusMessage && (
                            <Typography variant="body2">{purchaseStatusMessage}</Typography>
                        )}
                    </Box>
                ) : (
                    t('billing.buyQuestions', { count: selectedPlan.credits })
                )}
            </Button>


        </Box>)
    }

    const displayPaymentComponent = () => {
        return (
            <Box sx={{ p: padding }}>
                <Typography variant="h2" sx={{ textAlign: 'center', mb: 2, fontSize: '1.2rem' }}>
                    {t('billing.title')}
                </Typography>

                <Box>
                    <Stripe
                        onSuccess={handleSuccess}
                        priceId={selectedProductId}
                    />
                </Box>
            </Box>
        );
    }


    return (
        <div>
            {displayPriceSelector && displayPriceComponent()}
            {displayPaymentSelector && displayPaymentComponent()}
        </div>
    );
}

